import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { MarkersService } from '../../../../core/services/wrench-services/markers.service';
import { GlobeService } from '../../../../core/services/globe.service';
import * as Cesium from 'cesium';
import { MarkersParentService } from '../../../../core/services/wrench-services/markers-parent.service';
import { Subscription } from 'rxjs';
import { Marker } from '../../../../core/interfaces/marker.interface';
import { ReportMapEntity } from '../../../../core/services/report-map-entity.service';
import { CameraService } from '../../../../core/services/camera.service';
import { CoreService } from '../../../../core/services/core.service';
import { WindowService } from '../../../../core/services/window.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataContextService } from '../../../../core/services/data-context.service';
import { TimelineService } from '../../../../core/services/timeline.service';

@Component({
  selector: 'app-markers-palette',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './markers-palette.component.html',
  styleUrl: './markers-palette.component.css'
})
export class MarkersPaletteComponent implements OnInit, OnDestroy {
  markers: Marker[] = [];
  private subscriptions: Subscription[] = [];
  private viewer: any;
  private cesiumHandler: Cesium.ScreenSpaceEventHandler | null = null;
  markerColors = ['blue', 'red', 'yellow', 'green'];
  selectedColor: string | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() markerSelected = new EventEmitter<{ name: string; url: string }>();

  constructor(
    private markersService: MarkersService,
    private markersParentService: MarkersParentService,
    private globeService: GlobeService,
    private cameraService: CameraService,
    private coreService: CoreService,
    private windowService: WindowService,
    private dataContext: DataContextService,
    private modalService: NgbModal,
    private timelineService: TimelineService
  ) {}

  ngOnInit(): void {
    this.viewer = this.globeService.getViewer();
    if (!this.viewer) {
      console.error('Cesium viewer not initialized');
      return;
    }

    // Subscribe to markers updates
    this.subscriptions.push(
      this.markersService.getMarkers().subscribe(
        markers => this.markers = markers
      )
    );

    this.subscriptions.push(
      this.markersService.getSelectedMarkerType().subscribe(
        type => {
          this.selectedColor = type;
          this.cleanupCesiumHandler();
          if (type) {
            this.setupMarkerPlacement();
          }
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.cleanupCesiumHandler();
  }

  private setupMarkerPlacement(): void {
    if (!this.viewer) return;

    this.cesiumHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas);

    this.cesiumHandler.setInputAction((event: any) => {
      const position = this.viewer.camera.pickEllipsoid(event.position);
      if (position) {
        this.placeMarker(position);
        // this.markersService.setSelectedMarkerType(null); // Deselect after placing
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  private cleanupCesiumHandler(): void {
    if (this.cesiumHandler) {
      this.cesiumHandler.destroy();
      this.cesiumHandler = null;
    }
  }
  private placeMarker(position: Cesium.Cartesian3): void {
    if (!this.selectedColor || !this.viewer) return;

    const cartographic = Cesium.Cartographic.fromCartesian(position);
    const latitude = Cesium.Math.toDegrees(cartographic.latitude);
    const longitude = Cesium.Math.toDegrees(cartographic.longitude);

    const markerId = 'marker-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
    const markerName = `${this.selectedColor.charAt(0).toUpperCase() + this.selectedColor.slice(1)} Marker`;
    const iconUrl = `assets/plugins/images/${this.selectedColor}_marker.svg`;

    const reportMarker = new ReportMapEntity(
      markerId,
      markerName,
      'Plugins//Markers',
      iconUrl,
      latitude,
      longitude,
      0,
      new Date(),
      '',this.cameraService,this.coreService, this.windowService,
      this.dataContext, this.modalService, this.timelineService
    );
    reportMarker.setSource('Markers Plugin');
    reportMarker.description = `This is a ${markerName}.`;

    const cesiumEntity = reportMarker.getCesiumEntity();
    cesiumEntity.description = reportMarker.description;

    this.viewer.entities.add(cesiumEntity);

    const newMarker: Marker = {
      id: markerId,
      name: markerName,
      latitude,
      longitude,
      iconUrl,
      timestamp: Date.now(),
      source: 'Markers Plugin',
      type: this.selectedColor as 'blue' | 'red' | 'yellow' | 'green'
    };
    this.markersService.addMarker(newMarker);
    this.markersParentService.addMarkerToMap(newMarker);

    
    this.markerSelected.emit({
      name: markerName,
      url: iconUrl
    });
  }

  selectMarker(color: string) {
    this.selectedColor = color;
    this.markersService.setSelectedMarkerType(color);
    this.markerSelected.emit({
      name: `${color.charAt(0).toUpperCase() + color.slice(1)} Marker`,
      url: `assets/plugins/images/${color}_marker.svg`
    });
  }

  removeMarker(markerId: string): void {
    if (this.viewer) {
      this.viewer.entities.removeById(markerId);
    }
    this.markersService.removeMarker(markerId);
  }

  onCloseClick(): void {
    this.markersService.setSelectedMarkerType(null);
    this.close.emit();
    this.cleanupCesiumHandler();
  }
}
